import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { BellIcon, CheckIcon } from '@heroicons/react/24/outline'
import { formatDistanceToNow } from 'date-fns'
import { Pagination } from '@thinknimble/tn-models'
import { useTranslation } from 'react-i18next'
import { cn } from 'src/utils/style'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { notificationApi, notificationQueries } from 'src/services/notifications'
import type { Notification } from 'src/services/notifications'
import { Button } from 'src/components/button'
import { PaginationComponent } from 'src/components/pagination'

export const NotificationsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [pagination, setPagination] = useState(new Pagination({ page: 1, size: 20 }))

  const { data, isLoading } = useQuery(
    notificationQueries.list({
      pagination,
      paginationCallback: (serverPagination) => {
        setPagination(serverPagination)
      },
    }),
  )

  const { data: unreadData } = useQuery(notificationQueries.unreadCount())

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationApi.csc.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueries.all() })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationApi.csc.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueries.all() })
    },
  })

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id)
    }
    const url = notification.metadata?.actionUrl
    if (url && typeof url === 'string') {
      navigate(url)
    }
  }

  const notifications = data?.results ?? []
  const unreadCount = unreadData?.count ?? 0

  return (
    <>
      <PageHeader
        title={t('notifications.title')}
        actions={
          unreadCount > 0 && (
            <Button
              variant="ghost"
              icon={<CheckIcon />}
              onClick={() => markAllReadMutation.mutate()}
            >
              {t('notifications.markAllRead')}
            </Button>
          )
        }
      />
      <MainContent>
        <div className="w-full">
          {isLoading ? (
            <div className="py-12 text-center text-text-placeholder">{t('common.loading')}</div>
          ) : notifications.length === 0 ? (
            <div className="py-12 text-center">
              <BellIcon className="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
              <p className="mt-3 text-text-placeholder">{t('notifications.empty')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.map((notification) => (
                <Button
                  variant="unstyled"
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    'flex w-full items-start justify-start gap-3 rounded-lg border border-border bg-surface p-4 text-left shadow-sm transition-colors hover:bg-surface-hover',
                    !notification.isRead && 'border-l-4 border-primary-500',
                  )}
                >
                  {!notification.isRead && (
                    <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-primary-500" />
                  )}
                  {notification.isRead && <span className="mt-1.5 h-2.5 w-2.5 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'text-sm',
                        notification.isRead
                          ? 'text-text-secondary'
                          : 'font-medium text-text-primary',
                      )}
                    >
                      {notification.title}
                    </p>
                    <p className="mt-1 text-sm text-text-placeholder">{notification.body}</p>
                    <span className="mt-1 block text-xs text-text-tertiary">
                      {notification.created
                        ? formatDistanceToNow(new Date(notification.created), { addSuffix: true })
                        : ''}
                    </span>
                  </div>
                </Button>
              ))}
              {pagination.totalCount > pagination.size && (
                <div className="pt-4">
                  <PaginationComponent
                    pagination={pagination}
                    onNextPage={() =>
                      setPagination(new Pagination({ ...pagination, page: pagination.page + 1 }))
                    }
                    onPrevPage={() =>
                      setPagination(new Pagination({ ...pagination, page: pagination.page - 1 }))
                    }
                    onGoToPage={(page) => setPagination(new Pagination({ ...pagination, page }))}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </MainContent>
    </>
  )
}
